import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSeo from '../../seo/useSeo';
import * as PortOne from '@portone/browser-sdk/v2';
import PhoneNumberPopup, {
  normalizePhone,
  isValidPhone,
  formatPhoneDisplay,
} from '../../components/PhoneNumberPopup';
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineUsers,
  HiOutlineBanknotes,
  HiOutlineClock,
  HiOutlineChevronDown,
} from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import Header from '../../components/Header';
import Login from '../../components/Login';
import SafeAvatar from '../../components/SafeAvatar';
import AvatarEditPopup, { deriveAvatarInitial } from '../../components/AvatarEditPopup';
import './Subscription.css';
import '../dashboard/Dashboard.css';

function StatusCalendarIcon() {
  return (
    <svg
      className="status-icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="4" width="18" height="18" rx="3" ry="3" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <circle cx="12" cy="15" r="2.5" />
    </svg>
  );
}

// USD 포맷터 — 챌린지 수익/정산은 모두 달러로 표기 (소수점 1자리)
const formatUSD = (amount) =>
  `US $${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;

// 챌린지 탭 mock 데이터 — 추후 실제 테이블 생기면 fetch 로 교체. 금액은 USD.
const MOCK_CHALLENGES = [
  {
    id: 'ch-1',
    name: '4주 운동 챌린지',
    monthlyPrice: 9.9,
    subscribers: [
      { id: 's1', name: '김유진', startedAt: '2026-03-12', status: 'active' },
      { id: 's2', name: '이주헌', startedAt: '2026-04-18', status: 'active' },
      { id: 's3', name: '박서연', startedAt: '2026-05-02', status: 'active' },
      { id: 's4', name: '최민호', startedAt: '2026-05-21', status: 'active' },
    ],
  },
  {
    id: 'ch-2',
    name: '1년 결혼 준비 템플릿',
    monthlyPrice: 14.9,
    subscribers: [
      { id: 's5', name: '윤지수', startedAt: '2026-04-02', status: 'active' },
      { id: 's6', name: '한도윤', startedAt: '2026-06-01', status: 'active' },
    ],
  },
];
// 참가한(구독한) 챌린지 mock — 추후 실제 테이블 생기면 fetch 로 교체. 금액은 USD.
const MOCK_JOINED_CHALLENGES = [
  {
    id: 'jc-1',
    name: '21일 미라클 모닝',
    creator: '박서연',
    monthlyPrice: 7.9,
    joinedAt: '2026-05-10',
  },
  {
    id: 'jc-2',
    name: '주 3회 러닝 챌린지',
    creator: '김도훈',
    monthlyPrice: 9.9,
    joinedAt: '2026-06-01',
  },
];
const MOCK_PAYOUT_HISTORY = [
  {
    id: 'p1',
    challenge: '4주 운동 챌린지',
    requestedAt: '2026-05-20',
    amount: 19.8,
    status: 'paid',
    paidAt: '2026-05-23',
  },
  {
    id: 'p2',
    challenge: '1년 결혼 준비 템플릿',
    requestedAt: '2026-06-02',
    amount: 14.9,
    status: 'pending',
    paidAt: null,
  },
];

// PortOne(포트원) V2 — KG이니시스 정기결제(빌링키) 설정
// 결제 금액은 서버(portone.controller.js PLAN_AMOUNTS)에서 결정 — 위변조 방지.
const PORTONE_CONFIG = {
  storeId: process.env.REACT_APP_PORTONE_STORE_ID,
  channelKey: process.env.REACT_APP_PORTONE_CHANNEL_KEY,
};
// 카드사 심사용 임시: 연간 플랜 숨김(월간만). Polar 복귀 시 true 로 원복.
const SHOW_YEARLY = false;
// 챌린지 탭(현재 mock 크리에이터 수익 UI)은 2단계에서 실제 구축 시 노출. 지금은 숨김.
const SHOW_CHALLENGE_TAB = false;
// 정기결제 금액(원) — 서버 portone.controller.js PLAN_AMOUNTS 와 반드시 동일.
const PORTONE_SUB_AMOUNTS = { monthly: 12000, yearly: 114000 };

function Subscription() {
  const { t } = useTranslation('subscription');
  // 로그인 이후 화면 — 색인 대상에서 제외
  useSeo({ page: 'profile', path: '/profile', noindex: true });
  const { user, profile, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showLogin, setShowLogin] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showPricingPopup, setShowPricingPopup] = useState(false);
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [phonePopupOpen, setPhonePopupOpen] = useState(false);
  const pendingPayRef = useRef(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [invoiceCountAtCheckout, setInvoiceCountAtCheckout] = useState(null);
  // 탭
  const [activeTab, setActiveTab] = useState('subscription');
  // 닉네임 인라인 수정
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  // 휴대폰 번호 인라인 수정 (닉네임과 동일 패턴)
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editingPhone, setEditingPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  // 정산 계좌 입력
  const [payoutBank, setPayoutBank] = useState('');
  const [payoutAcct, setPayoutAcct] = useState('');
  const [payoutHolder, setPayoutHolder] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutErrors, setPayoutErrors] = useState({
    bank: '',
    acct: '',
    holder: '',
  });
  const [payoutHistory] = useState(MOCK_PAYOUT_HISTORY);
  // 챌린지 아코디언 — 펼쳐진 챌린지 id 집합
  const [expandedChallenges, setExpandedChallenges] = useState(() => new Set());

  const toggleChallenge = (id) => {
    setExpandedChallenges((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 프로필 로드되면 정산 계좌 입력 채움
  useEffect(() => {
    if (!profile) return;
    setPayoutBank(profile.payout_bank_name || '');
    setPayoutAcct(profile.payout_account_number || '');
    setPayoutHolder(profile.payout_account_holder || '');
  }, [profile]);

  const avatarUrl = profile?.updated_avatar_url;
  const displayName = profile?.updated_display_name || '';
  const avatarSrcForRender = (() => {
    if (typeof avatarUrl === 'string' && avatarUrl.trim()) return avatarUrl;
    const initial = deriveAvatarInitial(displayName);
    return initial && initial !== '?' ? initial : null;
  })();

  const fetchSubscriptionStatus = async () => {
    if (!user) return;
    try {
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (subError && subError.code !== 'PGRST116') {
        console.error('구독 정보 조회 오류:', subError);
      }
      setSubscription(subData || null);
    } catch (error) {
      console.error('구독 상태 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    if (!user) return;
    try {
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('subscription_payments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (invoiceError && invoiceError.code !== 'PGRST116') {
        console.error('청구서 조회 오류:', invoiceError);
      }
      setInvoices(invoiceData || []);
    } catch (error) {
      console.error('청구서 조회 오류:', error);
    }
  };

  const fetchSubscriptionData = async () => {
    await fetchSubscriptionStatus();
    await fetchInvoices();
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchSubscriptionData();

    // 구독 상태: 3초마다 갱신 (빠른 반영)
    const statusPoll = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchSubscriptionStatus();
      }
    }, 3000);

    // 청구서: 15초마다 갱신
    const invoicePoll = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchInvoices();
      }
    }, 15000);

    // 탭 복귀 시 즉시 전체 재조회
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchSubscriptionData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(statusPoll);
      clearInterval(invoicePoll);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 결제 성공 후 리다이렉트 처리 - URL 파라미터 감지만 담당
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (searchParams.get('success') === 'true' && !isPolling) {
      setShowSuccessMessage(true);
      setInvoiceCountAtCheckout(invoices.length);
      setIsPolling(true);
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // PortOne 모바일 리다이렉트 복귀 처리 (?portone=billing&paymentId=...&billingKey=...&code=...)
  useEffect(() => {
    if (searchParams.get('portone') !== 'billing') return;
    const paymentId = searchParams.get('paymentId');
    const billingKey = searchParams.get('billingKey');
    const code = searchParams.get('code');
    setSearchParams({});
    if (code) {
      alert(searchParams.get('message') || t('dialog.paymentFailed'));
      return;
    }
    if (paymentId) finalizeBilling(paymentId, billingKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 폴링 로직 - isPolling 상태로 제어
  useEffect(() => {
    if (!isPolling || !user) return;

    let attempts = 0;
    const maxAttempts = 15;
    const interval = 2000; // 2초 간격

    const pollSubscription = setInterval(async () => {
      attempts++;
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (data && !error) {
          clearInterval(pollSubscription);
          setSubscription(data);
          setIsPolling(false);
          fetchSubscriptionData();
          // 안내 메시지는 청구서 도착 시 자동으로 사라짐 (useEffect에서 처리)
        }
      } catch (e) {
        // 무시, 다음 폴링에서 재시도
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollSubscription);
        setIsPolling(false);
        fetchSubscriptionData();
      }
    }, interval);

    return () => clearInterval(pollSubscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, user]);

  // 새 청구서 도착 시 안내 메시지 숨김
  useEffect(() => {
    if (
      showSuccessMessage &&
      invoiceCountAtCheckout !== null &&
      invoices.length > invoiceCountAtCheckout
    ) {
      setShowSuccessMessage(false);
      setInvoiceCountAtCheckout(null);
    }
  }, [invoices, showSuccessMessage, invoiceCountAtCheckout]);

  const handleSubscribe = () => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    setShowPricingPopup(true);
  };

  // AndPay 결제 완료(창에서 이미 결제됨) → 서버 검증 → 성공 배너 + 폴링 시작
  const finalizeBilling = async (paymentId, billingKey) => {
    try {
      const res = await fetch('/api/portone/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId, billingKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setShowSuccessMessage(true);
        setInvoiceCountAtCheckout(invoices.length);
        setIsPolling(true);
      } else {
        console.error('결제 검증 실패:', data);
        alert(data.error || t('dialog.paymentError'));
      }
    } catch (e) {
      console.error('결제 검증 오류:', e);
      alert(t('dialog.paymentError'));
    }
  };

  // 휴대폰 번호가 있으면 바로 결제, 없으면 팝업으로 1회 입력받은 뒤 진행.
  const ensurePhoneThenPay = (payWithPhone) => {
    const phone = profile?.phone_number;
    if (phone) {
      payWithPhone(phone);
      return;
    }
    pendingPayRef.current = payWithPhone;
    setPhonePopupOpen(true);
  };

  const handleCheckout = (plan) => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    if (!PORTONE_CONFIG.storeId || !PORTONE_CONFIG.channelKey) {
      alert(t('dialog.paymentNotConfigured'));
      return;
    }
    setShowPricingPopup(false);
    ensurePhoneThenPay((phone) => runCheckout(plan, phone));
  };

  const runCheckout = async (plan, phone) => {
    const amount = PORTONE_SUB_AMOUNTS[plan] ?? PORTONE_SUB_AMOUNTS.monthly;
    const paymentId = `lansly-sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const orderName = plan === 'yearly' ? t('pricing.yearlyName') : t('pricing.monthlyName');

    try {
      // 빌링키 발급 + 첫 결제 동시(정기결제창에 금액 표시). 빌링 채널(INIBillTst) 사용.
      const response = await PortOne.requestIssueBillingKeyAndPay({
        storeId: PORTONE_CONFIG.storeId,
        channelKey: PORTONE_CONFIG.channelKey,
        billingKeyAndPayMethod: 'CARD',
        paymentId,
        orderName,
        totalAmount: amount,
        currency: 'CURRENCY_KRW',
        customer: { email: user.email, phoneNumber: phone },
        customData: JSON.stringify({ userId: user.id, plan }),
        // 모바일: 완료 후 이 URL 로 리다이렉트 (아래 useEffect 에서 처리).
        redirectUrl: `${window.location.origin}/profile?portone=billing`,
      });

      // 데스크톱은 Promise 로 결과 반환. response.code 가 있으면 취소/실패.
      if (response.code != null) {
        alert(response.message || t('dialog.paymentFailed'));
        return;
      }
      await finalizeBilling(response.paymentId, response.billingKey);
    } catch (e) {
      console.error('정기결제 오류:', e);
      alert(t('dialog.paymentError'));
    }
  };

  /*
  const handleManagePlan = async () => {
    if (!user) return;

    try {
      const response = await fetch(`/api/polar/portal?email=${encodeURIComponent(user.email)}`);
      const data = await response.json();

      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        alert('고객 포털을 열 수 없습니다.');
      }
    } catch (error) {
      console.error('고객 포털 오류:', error);
      alert('고객 포털을 열 수 없습니다.');
    }
  };
  */

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    if (!window.confirm(t('dialog.cancelConfirm'))) {
      return;
    }

    try {
      const providerBase = subscription?.provider === 'portone' ? '/api/portone' : '/api/polar';
      const response = await fetch(`${providerBase}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('구독 취소 응답이 JSON이 아닙니다:', response.status, contentType);
        alert(t('dialog.cancelHttpError'));
        return;
      }

      const data = await response.json();

      if (data.success) {
        alert(t('dialog.canceled', { date: formatDate(data.currentPeriodEnd) }));
        fetchSubscriptionData();
      } else {
        alert(data.error || t('dialog.cancelFailed'));
      }
    } catch (error) {
      console.error('구독 취소 오류:', error);
      alert(t('dialog.cancelError'));
    }
  };

  const handleRenewSubscription = async () => {
    // 취소 예정 상태(기간 남음)이면 취소 철회, 아니면 새 결제
    if (subscription?.cancel_at_period_end && subscription?.status === 'active') {
      if (!window.confirm(t('dialog.renewConfirm'))) {
        return;
      }
      try {
        const providerBase = subscription?.provider === 'portone' ? '/api/portone' : '/api/polar';
        const response = await fetch(`${providerBase}/reactivate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        });
        const data = await response.json();

        if (data.success) {
          alert(t('dialog.renewed'));
          fetchSubscriptionData();
        } else {
          alert(data.error || t('dialog.renewFailed'));
        }
      } catch (error) {
        console.error('구독 갱신 오류:', error);
        alert(t('dialog.renewError'));
      }
    } else {
      // 구독이 만료된 경우 새 결제
      handleSubscribe();
    }
  };

  // ====== 계정 액션 ======
  const handleLogout = async () => {
    if (!window.confirm(t('dialog.logoutConfirm'))) return;
    try {
      await signOut();
    } catch (e) {
      console.error('로그아웃 에러:', e?.message || e);
    }
    navigate('/');
  };

  const handleWithdraw = async () => {
    if (!user?.id) return;
    if (!window.confirm(t('dialog.withdrawConfirm'))) {
      return;
    }
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ withdrawal_requested_at: new Date(), updated_at: new Date() })
        .eq('id', user.id);
      if (error) throw error;
      alert(t('dialog.withdrawDone'));
      try {
        await signOut();
      } catch (signErr) {
        console.error('탈퇴 후 로그아웃 에러:', signErr?.message || signErr);
      }
      navigate('/');
    } catch (e) {
      console.error('회원 탈퇴 에러:', e?.message || e);
      alert(t('dialog.withdrawError'));
    }
  };

  const startEditName = () => {
    setEditingName(displayName);
    setNameError('');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!user?.id || nameSaving) return;
    const next = editingName.trim();
    if (!next) {
      setNameError(t('dialog.nameRequired'));
      return;
    }
    if (next === displayName) {
      setNameError(t('dialog.nameSame'));
      return;
    }
    setNameSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ updated_display_name: next, updated_at: new Date() })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile?.();
      setIsEditingName(false);
    } catch (e) {
      console.error('닉네임 변경 에러:', e?.message || e);
      alert(t('dialog.nameError'));
    } finally {
      setNameSaving(false);
    }
  };

  const startEditPhone = () => {
    setEditingPhone(formatPhoneDisplay(profile?.phone_number || ''));
    setPhoneError('');
    setIsEditingPhone(true);
  };

  const handleSavePhone = async () => {
    if (!user?.id || phoneSaving) return;
    const next = normalizePhone(editingPhone);
    if (!isValidPhone(next)) {
      setPhoneError(t('dialog.phoneInvalid'));
      return;
    }
    if (next === (profile?.phone_number || '')) {
      setPhoneError(t('dialog.phoneSame'));
      return;
    }
    setPhoneSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ phone_number: next, updated_at: new Date() })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile?.();
      setIsEditingPhone(false);
    } catch (e) {
      console.error('휴대폰 변경 에러:', e?.message || e);
      alert(t('dialog.phoneError'));
    } finally {
      setPhoneSaving(false);
    }
  };

  // ====== 정산 계좌 ======
  const handleSavePayoutInfo = async () => {
    if (!user?.id || payoutSaving) return;
    const bank = payoutBank.trim();
    const acct = payoutAcct.trim();
    const holder = payoutHolder.trim();
    // 빈 칸 검증 — alert 대신 인라인 에러
    const nextErrors = {
      bank: bank ? '' : t('payout.bankRequired'),
      acct: acct ? '' : t('payout.accountRequired'),
      holder: holder ? '' : t('payout.holderRequired'),
    };
    setPayoutErrors(nextErrors);
    if (nextErrors.bank || nextErrors.acct || nextErrors.holder) return;

    setPayoutSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          payout_bank_name: bank,
          payout_account_number: acct,
          payout_account_holder: holder,
          updated_at: new Date(),
        })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile?.();
      alert(t('payout.saved'));
    } catch (e) {
      console.error('정산 계좌 저장 에러:', e?.message || e);
      alert(t('payout.saveError'));
    } finally {
      setPayoutSaving(false);
    }
  };

  // 정산 가능액 — 한 달 이상 지난 구독자의 1달치 수익만 합산 (mock 계산)
  const computePayoutEligible = (challenge) => {
    const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const eligible = (challenge.subscribers || []).filter(
      (s) => s.status === 'active' && now - new Date(s.startedAt).getTime() >= ONE_MONTH_MS
    );
    return { count: eligible.length, amount: eligible.length * challenge.monthlyPrice };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return t('date.format', {
      y: date.getFullYear(),
      m: date.getMonth() + 1,
      d: date.getDate(),
    });
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (amount == null) return '-';
    // 원화(포트원/KG이니시스)는 정수 원 단위로 그대로 표기
    if (String(currency).toLowerCase() === 'krw') {
      return `₩${Number(amount).toLocaleString('ko-KR')}`;
    }
    // Polar(USD)는 센트 단위 저장 → 달러 환산
    return `US $${(amount / 100).toFixed(1)}`;
  };

  const getStatusText = (status) => {
    const statusKeys = {
      paid: 'invoiceStatus.paid',
      pending: 'invoiceStatus.pending',
      failed: 'invoiceStatus.failed',
      refunded: 'invoiceStatus.refunded',
      partially_refunded: 'invoiceStatus.partially_refunded',
      canceled: 'invoiceStatus.canceled',
      revoked: 'invoiceStatus.revoked',
    };
    return statusKeys[status] ? t(statusKeys[status]) : status;
  };

  const getStatusClass = (status) => {
    const classMap = {
      paid: 'status-paid',
      pending: 'status-pending',
      failed: 'status-failed',
      refunded: 'status-refunded',
      partially_refunded: 'status-refunded',
      canceled: 'status-canceled',
      revoked: 'status-revoked',
    };
    return classMap[status] || '';
  };

  // 구독 상태 판단
  const isSubscribed = subscription?.status === 'active';
  const isCanceled = subscription?.cancel_at_period_end === true;
  const isPremium = isSubscribed || isCanceled;

  // 플랜 정보
  const planName = isPremium ? t('plan.premium') : t('plan.free');
  const planPeriod = isPremium
    ? subscription?.interval === 'year'
      ? t('plan.yearly')
      : t('plan.monthly')
    : t('plan.freeForever');

  // 상태 메시지 및 액션
  let statusMessage = '';
  let statusAction = null;

  if (!isPremium) {
    statusMessage = t('status.startPrompt');
    statusAction = { label: t('status.subscribe'), onClick: handleSubscribe, type: 'primary' };
  } else if (isCanceled) {
    const cancelDate = formatDate(subscription?.current_period_end);
    statusMessage = t('status.willCancel', { date: cancelDate });
    statusAction = { label: t('status.renew'), onClick: handleRenewSubscription, type: 'primary' };
  } else {
    const nextBillingDate = formatDate(subscription?.current_period_end);
    statusMessage = t('status.nextBilling', { date: nextBillingDate });
    statusAction = { label: t('status.cancel'), onClick: handleCancelSubscription, type: 'text' };
  }

  // 비로그인 상태
  if (!user) {
    return (
      <div className="subscription-page">
        <Header
          onMenuClick={() => setShowPopup(true)}
          showPopup={showPopup}
          onClosePopup={() => setShowPopup(false)}
          onLoginClick={() => setShowLogin(true)}
        />
        <div className="subscription-container">
          <h1 className="subscription-title">{t('profile')}</h1>
          <div className="subscription-login-prompt">
            <p>{t('loginRequired')}</p>
            <button className="subscription-login-btn" onClick={() => setShowLogin(true)}>
              {t('login')}
            </button>
          </div>
        </div>
        <Login isOpen={showLogin} onClose={() => setShowLogin(false)} />
      </div>
    );
  }

  // 로딩 상태
  if (loading) {
    return (
      <div className="subscription-page">
        <Header
          onMenuClick={() => setShowPopup(true)}
          showPopup={showPopup}
          onClosePopup={() => setShowPopup(false)}
          onLoginClick={() => setShowLogin(true)}
        />
        <div className="subscription-container">
          <div className="subscription-loading" role="status" aria-label={t('loading')}>
            <div className="subscription-loading-spinner" aria-hidden />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <Header
        onMenuClick={() => setShowPopup(true)}
        showPopup={showPopup}
        onClosePopup={() => setShowPopup(false)}
        onLoginClick={() => setShowLogin(true)}
      />

      <div className="subscription-container">
        {/* 상단 타이틀 행 — 좌측 '프로필', 우측 로그아웃 */}
        <div className="profile-titlebar">
          <h1 className="subscription-title">{t('profile')}</h1>
          <button
            type="button"
            className="profile-logout-btn"
            onClick={handleLogout}
            aria-label={t('logout')}
          >
            <HiOutlineArrowRightOnRectangle aria-hidden />
            {t('logout')}
          </button>
        </div>

        {/* ====== Account 섹션 — 아바타 + (PC: 닉네임/이메일 2-column 그리드, 모바일: 세로) ====== */}
        <section className="profile-account-card">
          <button
            type="button"
            className="profile-account-avatar-btn"
            onClick={() => setShowAvatarEdit(true)}
            aria-label={t('editThumbnail')}
            title={t('editThumbnail')}
          >
            <SafeAvatar
              src={avatarSrcForRender}
              alt="Profile"
              className="profile-account-avatar"
              fallbackClassName="profile-account-avatar"
            />
          </button>
          <div className="profile-account-grid">
            <div className="profile-account-row">
              <label className="profile-account-label">{t('account.nickname')}</label>
              {isEditingName ? (
                <div className="profile-account-edit">
                  <input
                    type="text"
                    className={`profile-account-input${nameError ? ' profile-account-input--error' : ''}`}
                    value={editingName}
                    onChange={(e) => {
                      setEditingName(e.target.value);
                      if (nameError) setNameError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                    }}
                    placeholder={t('account.namePlaceholder')}
                    autoFocus
                    aria-invalid={!!nameError}
                  />
                  {nameError && <div className="profile-account-error">{nameError}</div>}
                  <div className="profile-account-edit-actions">
                    <button
                      type="button"
                      className="profile-btn-outline"
                      onClick={() => {
                        setIsEditingName(false);
                        setNameError('');
                      }}
                      disabled={nameSaving}
                    >
                      {t('account.cancel')}
                    </button>
                    <button
                      type="button"
                      className="profile-btn-primary"
                      onClick={handleSaveName}
                      disabled={nameSaving}
                    >
                      {nameSaving ? t('account.saving') : t('account.save')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="profile-account-display-row">
                  <span className="profile-account-value">{displayName || '—'}</span>
                  <button
                    type="button"
                    className="name-edit-btn"
                    onClick={startEditName}
                    aria-label={t('account.editName')}
                    title={t('account.editName')}
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="profile-account-row">
              <label className="profile-account-label">{t('account.email')}</label>
              <span className="profile-account-value profile-account-email">{user.email}</span>
            </div>
            <div className="profile-account-row">
              <label className="profile-account-label">{t('account.phone')}</label>
              {isEditingPhone ? (
                <div className="profile-account-edit">
                  <input
                    type="tel"
                    inputMode="numeric"
                    className={`profile-account-input${phoneError ? ' profile-account-input--error' : ''}`}
                    value={editingPhone}
                    onChange={(e) => {
                      setEditingPhone(formatPhoneDisplay(e.target.value));
                      if (phoneError) setPhoneError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSavePhone();
                    }}
                    placeholder={t('account.phonePlaceholder')}
                    autoFocus
                    maxLength={13}
                    aria-invalid={!!phoneError}
                  />
                  {phoneError && <div className="profile-account-error">{phoneError}</div>}
                  <div className="profile-account-edit-actions">
                    <button
                      type="button"
                      className="profile-btn-outline"
                      onClick={() => {
                        setIsEditingPhone(false);
                        setPhoneError('');
                      }}
                      disabled={phoneSaving}
                    >
                      {t('account.cancel')}
                    </button>
                    <button
                      type="button"
                      className="profile-btn-primary"
                      onClick={handleSavePhone}
                      disabled={phoneSaving}
                    >
                      {phoneSaving ? t('account.saving') : t('account.save')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="profile-account-display-row">
                  <span className="profile-account-value">
                    {profile?.phone_number ? formatPhoneDisplay(profile.phone_number) : '-'}
                  </span>
                  <button
                    type="button"
                    className="name-edit-btn"
                    onClick={startEditPhone}
                    aria-label={t('account.editPhone')}
                    title={t('account.editPhone')}
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ====== 탭 ====== */}
        <div className="profile-tabs" role="tablist" aria-label={t('tabs.aria')}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'subscription'}
            className={`profile-tab${activeTab === 'subscription' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            {t('tabs.subscription')}
          </button>
          {SHOW_CHALLENGE_TAB && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'challenge'}
              className={`profile-tab${activeTab === 'challenge' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('challenge')}
            >
              {t('tabs.challenge')}
            </button>
          )}
        </div>

        {/* ====== 구독 탭 ====== */}
        {activeTab === 'subscription' && (
          <div className="profile-tab-panel" role="tabpanel">
            {/* 결제 성공 메시지 */}
            {showSuccessMessage && (
              <div className="subscription-success-banner">
                <span className="success-icon">✓</span>
                <p>{t('success.banner')}</p>
              </div>
            )}

            {/* 플랜 정보 */}
            <div className="subscription-plan">
              <div className="plan-info">
                <div className="plan-logo">
                  L<span className="plan-logo-dot">.</span>
                </div>
                <div className="plan-details">
                  <h2 className="plan-name">{planName}</h2>
                  <p className="plan-period">{planPeriod}</p>
                </div>
              </div>
            </div>

            {/* 상태 배너 */}
            <div className="subscription-status">
              <div className="status-content">
                <StatusCalendarIcon />
                <p className="status-message">{statusMessage}</p>
              </div>
              {statusAction && (
                <button
                  className={`status-action-btn ${statusAction.type}`}
                  onClick={statusAction.onClick}
                >
                  {statusAction.label}
                </button>
              )}
            </div>

            {/* 청구서 섹션 */}
            <div className="subscription-invoices">
              <h2 className="invoices-title">{t('invoices.title')}</h2>

              {showSuccessMessage && (
                <p className="invoices-notice">{t('success.invoiceNotice')}</p>
              )}

              {invoices.length === 0 && !showSuccessMessage ? (
                <div className="invoices-empty">
                  <p>{t('invoices.empty')}</p>
                </div>
              ) : invoices.length === 0 ? null : (
                <div className="invoices-table-wrapper">
                  <table className="invoices-table">
                    <thead>
                      <tr>
                        <th>{t('invoices.date')}</th>
                        <th>{t('invoices.amount')}</th>
                        <th>{t('invoices.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td>{formatDate(invoice.created_at)}</td>
                          <td>{formatCurrency(invoice.amount, invoice.currency)}</td>
                          <td>
                            <span className={`invoice-status ${getStatusClass(invoice.status)}`}>
                              {getStatusText(invoice.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== 챌린지 탭 (mock) — 2단계 실제 구축 시 노출 ====== */}
        {SHOW_CHALLENGE_TAB && activeTab === 'challenge' && (
          <div className="profile-tab-panel" role="tabpanel">
            {/* 정산 계좌 정보 */}
            <div className="profile-section-card">
              <h3 className="profile-section-title">{t('payout.title')}</h3>
              <p className="profile-section-desc">{t('payout.desc')}</p>
              <div className="profile-payout-grid">
                <label className={`profile-field${payoutErrors.bank ? ' is-error' : ''}`}>
                  <span>{t('payout.bank')}</span>
                  <input
                    type="text"
                    value={payoutBank}
                    onChange={(e) => {
                      setPayoutBank(e.target.value);
                      if (payoutErrors.bank) setPayoutErrors((p) => ({ ...p, bank: '' }));
                    }}
                    placeholder={t('payout.bankPlaceholder')}
                  />
                  {payoutErrors.bank && (
                    <div className="profile-field-error">{payoutErrors.bank}</div>
                  )}
                </label>
                <label className={`profile-field${payoutErrors.acct ? ' is-error' : ''}`}>
                  <span>{t('payout.account')}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={payoutAcct}
                    onChange={(e) => {
                      setPayoutAcct(e.target.value);
                      if (payoutErrors.acct) setPayoutErrors((p) => ({ ...p, acct: '' }));
                    }}
                    placeholder={t('payout.accountPlaceholder')}
                  />
                  {payoutErrors.acct && (
                    <div className="profile-field-error">{payoutErrors.acct}</div>
                  )}
                </label>
                <label className={`profile-field${payoutErrors.holder ? ' is-error' : ''}`}>
                  <span>{t('payout.holder')}</span>
                  <input
                    type="text"
                    value={payoutHolder}
                    onChange={(e) => {
                      setPayoutHolder(e.target.value);
                      if (payoutErrors.holder) setPayoutErrors((p) => ({ ...p, holder: '' }));
                    }}
                    placeholder={t('payout.holderPlaceholder')}
                  />
                  {payoutErrors.holder && (
                    <div className="profile-field-error">{payoutErrors.holder}</div>
                  )}
                </label>
              </div>
              <div className="profile-section-actions">
                <button
                  type="button"
                  className="profile-btn-primary"
                  onClick={handleSavePayoutInfo}
                  disabled={payoutSaving}
                >
                  {payoutSaving ? t('payout.saving') : t('payout.save')}
                </button>
              </div>
            </div>

            {/* 챌린지별 현황 */}
            <div className="profile-section-card">
              <h3 className="profile-section-title">{t('challenge.runningTitle')}</h3>
              <p className="profile-section-desc">{t('challenge.runningDesc')}</p>
              <div className="profile-challenge-list">
                {MOCK_CHALLENGES.map((ch) => {
                  const subs = ch.subscribers || [];
                  const totalRevenue = subs.length * ch.monthlyPrice;
                  const { count: eligibleCount, amount: eligibleAmount } =
                    computePayoutEligible(ch);
                  const expanded = expandedChallenges.has(ch.id);
                  return (
                    <div
                      key={ch.id}
                      className={`profile-challenge-card${expanded ? ' is-open' : ''}`}
                    >
                      <button
                        type="button"
                        className="profile-challenge-head"
                        onClick={() => toggleChallenge(ch.id)}
                        aria-expanded={expanded}
                        aria-controls={`challenge-body-${ch.id}`}
                      >
                        <h4 className="profile-challenge-name">{ch.name}</h4>
                        <div className="profile-challenge-head-right">
                          <span className="profile-challenge-summary">
                            {t('challenge.headSummary', {
                              subscribers: subs.length,
                              eligible: eligibleCount,
                            })}
                          </span>
                          <span className="profile-challenge-price">
                            {t('challenge.perMonth', { price: formatUSD(ch.monthlyPrice) })}
                          </span>
                          <HiOutlineChevronDown className="profile-challenge-chevron" aria-hidden />
                        </div>
                      </button>
                      {expanded && (
                        <div id={`challenge-body-${ch.id}`} className="profile-challenge-body">
                          <div className="profile-challenge-stats">
                            <div className="profile-stat">
                              <span className="profile-stat-label">
                                <HiOutlineUsers aria-hidden /> {t('challenge.totalSubscribers')}
                              </span>
                              <strong className="profile-stat-value">
                                {t('challenge.count', { n: subs.length })}
                              </strong>
                            </div>
                            <div className="profile-stat">
                              <span className="profile-stat-label">
                                <HiOutlineBanknotes aria-hidden /> {t('challenge.cumulativeRevenue')}
                              </span>
                              <strong className="profile-stat-value">
                                {formatUSD(totalRevenue)}
                              </strong>
                            </div>
                            <div className="profile-stat">
                              <span className="profile-stat-label">
                                <HiOutlineClock aria-hidden /> {t('challenge.eligibleAmount')}
                              </span>
                              <strong className="profile-stat-value profile-stat-positive">
                                {formatUSD(eligibleAmount)}
                              </strong>
                              <span className="profile-stat-sub">
                                {t('challenge.ratio', {
                                  eligible: eligibleCount,
                                  total: subs.length,
                                })}
                              </span>
                            </div>
                          </div>

                          {/* 구독자 명단 */}
                          <div className="profile-subscriber-list">
                            <div className="profile-subscriber-list-head">
                              {t('challenge.subscriberList')}
                            </div>
                            {subs.length === 0 ? (
                              <p className="profile-subscriber-empty">
                                {t('challenge.noSubscribers')}
                              </p>
                            ) : (
                              <ul>
                                {subs.map((s) => {
                                  const days = Math.floor(
                                    (Date.now() - new Date(s.startedAt).getTime()) /
                                      (24 * 60 * 60 * 1000)
                                  );
                                  const isEligible = days >= 30;
                                  return (
                                    <li key={s.id} className="profile-subscriber-item">
                                      <SafeAvatar
                                        src={s.avatarUrl || deriveAvatarInitial(s.name)}
                                        alt=""
                                        className="profile-subscriber-avatar"
                                        fallbackClassName="profile-subscriber-avatar"
                                      />
                                      <span className="profile-subscriber-name">{s.name}</span>
                                      <span className="profile-subscriber-meta">
                                        {t('challenge.startedAt', { date: s.startedAt })}
                                      </span>
                                      <span
                                        className={`profile-subscriber-tag${isEligible ? ' is-eligible' : ''}`}
                                      >
                                        {isEligible ? t('challenge.eligible') : t('challenge.pending')}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 참가한 챌린지 */}
            <div className="profile-section-card">
              <h3 className="profile-section-title">{t('challenge.joinedTitle')}</h3>
              <p className="profile-section-desc">{t('challenge.joinedDesc')}</p>
              {MOCK_JOINED_CHALLENGES.length === 0 ? (
                <p className="profile-section-empty">{t('challenge.noJoined')}</p>
              ) : (
                <ul className="profile-joined-list">
                  {MOCK_JOINED_CHALLENGES.map((ch) => (
                    <li key={ch.id} className="profile-joined-item">
                      <div className="profile-joined-main">
                        <span className="profile-joined-name">{ch.name}</span>
                        <span className="profile-joined-creator">
                          {t('challenge.operatedBy', { name: ch.creator })}
                        </span>
                      </div>
                      <div className="profile-joined-right">
                        <span className="profile-joined-price">
                          {t('challenge.perMonth', { price: formatUSD(ch.monthlyPrice) })}
                        </span>
                        <span className="profile-joined-meta">
                          {t('challenge.joinedAt', { date: ch.joinedAt })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 정산 신청 내역 */}
            <div className="profile-section-card">
              <h3 className="profile-section-title">{t('payoutHistory.title')}</h3>
              {payoutHistory.length === 0 ? (
                <p className="profile-section-empty">{t('payoutHistory.empty')}</p>
              ) : (
                <div className="invoices-table-wrapper">
                  <table className="invoices-table">
                    <thead>
                      <tr>
                        <th>{t('payoutHistory.requestedAt')}</th>
                        <th>{t('payoutHistory.amount')}</th>
                        <th>{t('payoutHistory.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutHistory.map((row) => (
                        <tr key={row.id}>
                          <td>{row.requestedAt}</td>
                          <td>{formatUSD(row.amount)}</td>
                          <td>
                            <span
                              className={`invoice-status ${row.status === 'paid' ? 'status-paid-blue' : 'status-pending-green'}`}
                            >
                              {row.status === 'paid'
                                ? t('payoutHistory.paid', { date: row.paidAt })
                                : t('payoutHistory.reviewing')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 회원 탈퇴 — 컨테이너 맨 아래 좌측 (최대한 보이게) */}
        <div className="profile-bottom-bar">
          <button type="button" className="profile-withdraw-btn" onClick={handleWithdraw}>
            {t('withdraw')}
          </button>
        </div>
      </div>

      <Login isOpen={showLogin} onClose={() => setShowLogin(false)} />

      {phonePopupOpen && (
        <PhoneNumberPopup
          onClose={() => {
            pendingPayRef.current = null;
            setPhonePopupOpen(false);
          }}
          onSaved={(phone) => {
            setPhonePopupOpen(false);
            const run = pendingPayRef.current;
            pendingPayRef.current = null;
            run?.(phone);
          }}
        />
      )}

      {showAvatarEdit && (
        <AvatarEditPopup
          user={user}
          currentAvatarUrl={avatarUrl}
          displayName={displayName}
          onClose={() => setShowAvatarEdit(false)}
          onSaved={() => window.location.reload()}
        />
      )}

      {/* 구독 플랜 선택 팝업 — 대시보드 요금 팝업과 동일 마크업·스타일 */}
      {showPricingPopup && (
        <div
          className="claude-popup-overlay subscription-claude-tokens"
          onClick={() => setShowPricingPopup(false)}
        >
          <div
            className="claude-popup-modal claude-pricing-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="claude-popup-close" onClick={() => setShowPricingPopup(false)}>
              ×
            </button>
            <div className="claude-pricing-header">
              <h2 className="claude-pricing-title">{t('pricing.title')}</h2>
              <p className="claude-pricing-subtitle">{t('pricing.subtitle')}</p>
            </div>
            <div className="claude-pricing-plans">
              <div className="claude-pricing-plan">
                <div className="claude-plan-header">
                  <h3 className="claude-plan-name">{t('pricing.monthlyName')}</h3>
                  <div className="claude-plan-price">
                    <span className="claude-price-amount">₩12,000</span>
                    <span className="claude-price-period">{t('pricing.perMonth')}</span>
                  </div>
                  {/* <span className="claude-price-tax">+ Tax (국가별 상이)</span> */}
                </div>
                <ul className="claude-plan-features">
                  <li>{t('pricing.featProject')}</li>
                  <li>{t('pricing.featScheduling')}</li>
                  <li>{t('pricing.featDashboard')}</li>
                  <li>{t('pricing.featInvite')}</li>
                </ul>
                <button
                  className="claude-btn-outline claude-plan-btn"
                  onClick={() => handleCheckout('monthly')}
                >
                  {t('pricing.monthlyCta')}
                </button>
              </div>
              {SHOW_YEARLY && (
                <div className="claude-pricing-plan claude-plan-recommended">
                  <div className="claude-plan-badge">{t('pricing.recommended')}</div>
                  <div className="claude-plan-header">
                    <h3 className="claude-plan-name">{t('pricing.yearlyName')}</h3>
                    <div className="claude-plan-price">
                      <span className="claude-price-amount">₩9,500</span>
                      <span className="claude-price-period">{t('pricing.perMonth')}</span>
                    </div>
                    {/* <span className="claude-price-tax">+ Tax (국가별 상이)</span> */}
                    <div className="claude-plan-savings">{t('pricing.yearlySavings')}</div>
                  </div>
                  <ul className="claude-plan-features">
                    <li>{t('pricing.featProject')}</li>
                    <li>{t('pricing.featScheduling')}</li>
                    <li>{t('pricing.featDashboard')}</li>
                    <li>{t('pricing.featInvite')}</li>
                  </ul>
                  <button
                    className="claude-btn-primary claude-plan-btn"
                    onClick={() => handleCheckout('yearly')}
                  >
                    {t('pricing.yearlyCta')}
                  </button>
                </div>
              )}
            </div>
            <p className="claude-pricing-note">{t('pricing.note')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscription;
